import { call, put, takeLatest, select, delay } from "redux-saga/effects";
import { setPrefs, updatePrefsRequest, loadPrefsRequest } from "./prefsSlice";
import { setRecentSearches, loadRecentSearchesRequest, loadLocalRecentSearches } from "../recentSearches/recentSearchesSlice";
import userProfileService from "../../services/userProfileService";
import i18n from "../../i18n";

function* handleUpdatePrefs(action) {
  const newPrefs = action.payload;

  // console.log(`in handleUpdatePrefs: ${JSON.stringify(newPrefs)}`);
  
  // 1. Update local state
  // 1. Update local state
  yield put(setPrefs(newPrefs));

  // 2. Read normalized prefs from store and sync with UI
  const prefsState = yield select(state => state.prefs);
  if (prefsState && prefsState.lang) {
    i18n.changeLanguage(prefsState.lang);
  }

  // 3. Sync with backend using User Profile API
  try {
    // Get current user state to check if logged in
    const userState = yield select(state => state.user);
    
    if (userState?.user?.id) {
      // Update user profile with the normalized preferences from store
      // Build profile update using only the keys that were changed, but use the
      // normalized values from prefsState so backend gets canonical data.
      const changedKeys = Object.keys(newPrefs || {});
      const prefsToSend = changedKeys.reduce((acc, key) => {
        if (prefsState && typeof prefsState[key] !== 'undefined') {
          acc[key] = prefsState[key];
        }
        return acc;
      }, {});

      const profileUpdate = { prefs: prefsToSend };

      yield call(userProfileService.updateUserProfile, userState.user.id, profileUpdate);
      
      console.log('Preferences synced to backend successfully');

      // Reset manual flags since preferences are now synced, but not if they were just set
      const resetFlags = {};
      if (!newPrefs.langManuallySet) resetFlags.langManuallySet = false;
      if (!newPrefs.curManuallySet) resetFlags.curManuallySet = false;
      if (Object.keys(resetFlags).length > 0) {
        yield put(setPrefs(resetFlags));
      }
    } else {
      console.log('User not logged in, preferences saved locally only');
    }
  } catch (err) {
    console.error("Failed to sync preferences to backend:", err);
    // Note: We keep the local prefs update even if backend sync fails
    // This ensures the UI reflects the user's choice immediately
  }
}

/**
 * Handle user login/logout - automatically load preferences from backend or preserve local data
 */
function* handleUserLogin(action) {
  try {
    const token = action.payload;
    
    if (token) {
      // User is logging in - migrate local searches and load from backend
      // Give a small delay to ensure user state is updated
      yield delay(100);
      
      // Get current user state
      const userState = yield select(state => state.user);
      
      if (userState?.user?.id) {
        // Migrate any local searches to the backend before loading profile
        const localSearches = loadLocalRecentSearches();
        if (localSearches && localSearches.length > 0) {
          console.log(`Migrating ${localSearches.length} local searches to backend for user ${userState.user.id}`);
          const migrationSuccess = yield call([userProfileService, 'migrateLocalSearchHistory'], userState.user.id, localSearches);
          
          if (migrationSuccess) {
            console.log('Local search history migrated successfully');
            // Clear local searches after successful migration
            localStorage.removeItem('recentSearchesV2');
          } else {
            console.warn('Failed to migrate local search history');
          }
        }
      }
      
      // Dispatch loadPrefsRequest to load preferences from backend
      yield put(loadPrefsRequest());
      
      // Also load recent searches for authenticated users
      yield put(loadRecentSearchesRequest());
      
      console.log('Preferences and recent searches load triggered after user login');
    } else {
      // User is logging out - preserve local recent searches
      yield put(loadRecentSearchesRequest());
      console.log('User logged out - loading local recent searches from localStorage');
    }
  } catch (err) {
    console.error("Failed to trigger preferences load after login/logout:", err);
  }
}

function* handleLoadPrefs() {
  try {
    // Get current user state
    const userState = yield select(state => state.user);
    
    if (userState?.user?.id) {
      // Fetch user profile from backend
      const profile = yield call(userProfileService.getUserProfile, userState.user.id);
      
      if (profile?.user?.prefs && Object.keys(profile.user.prefs).length > 0) {
        // Use the backend prefs directly (already in short format: lang/cur)
        yield put(setPrefs(profile.user.prefs));
        
        // Update i18n directly for language changes
        if (profile.user.prefs.lang) {
          i18n.changeLanguage(profile.user.prefs.lang);
        }
        
        // Reset manual flags since we loaded from backend, but not if they were set
        const resetFlags = {};
        if (profile.user.prefs.langManuallySet !== true) resetFlags.langManuallySet = false;
        if (profile.user.prefs.curManuallySet !== true) resetFlags.curManuallySet = false;
        if (Object.keys(resetFlags).length > 0) {
          yield put(setPrefs(resetFlags));
        }
        
        console.log('Preferences loaded from backend');
      } else {
        console.log('No preferences found in backend or empty prefs object - keeping local preferences');
      }

      // Language auto-detection based on name is now performed by backend auth APIs.

      // Load recent searches from the profile data (only when user is logged in)
      if (profile?.profile?.recentSearches) {
        yield put(setRecentSearches(profile.profile.recentSearches));
        console.log(`Loaded ${profile.profile.recentSearches.length} recent searches from profile`);
      }
      // Note: We removed the automatic clearing of recent searches when backend has none
      // This prevents unwanted clearing during logout transitions
    }
    // Note: When no user is logged in, we don't dispatch any actions
    // This preserves the existing behavior expected by tests
  } catch (err) {
    console.error("Failed to load preferences from backend:", err);
    // Don't dispatch any actions when there's an API error - preserve existing test expectations
  }
}

export default function* prefsSaga() {
  yield takeLatest("prefs/updatePrefsRequest", handleUpdatePrefs);
  yield takeLatest("prefs/loadPrefsRequest", handleLoadPrefs);
  yield takeLatest("user/setUserFromToken", handleUserLogin);
}

// Export individual functions for testing
export { handleUpdatePrefs, handleLoadPrefs, handleUserLogin };
